/**
 * Cliente HTTP para la API. Centraliza:
 * - URL base (VITE_API_URL)
 * - inyección de Authorization
 * - manejo de errores
 */
const BASE = import.meta.env.VITE_API_URL || '';

const TOKEN_KEY = 'kb-token';

export const auth = {
  get token() {
    return localStorage.getItem(TOKEN_KEY);
  },
  setToken(t: string | null) {
    if (t) localStorage.setItem(TOKEN_KEY, t);
    else localStorage.removeItem(TOKEN_KEY);
  },
};

export class ApiError extends Error {
  status: number;
  data: unknown;
  constructor(message: string, status: number, data: unknown) {
    super(message);
    this.status = status;
    this.data = data;
  }
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  isFormData?: boolean;
}

export async function api<T = unknown>(path: string, opts: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...(opts.headers as Record<string, string> | undefined),
  };
  const token = auth.token;
  if (token) headers.Authorization = `Bearer ${token}`;

  let body: BodyInit | undefined;
  if (opts.body !== undefined) {
    if (opts.isFormData) {
      body = opts.body as BodyInit;
    } else {
      headers['Content-Type'] = 'application/json';
      body = JSON.stringify(opts.body);
    }
  }

  const res = await fetch(`${BASE}${path}`, { ...opts, headers, body });

  if (res.status === 401) {
    auth.setToken(null);
    if (location.pathname !== '/login') location.href = '/login';
  }

  const contentType = res.headers.get('content-type') ?? '';
  const data = contentType.includes('application/json') ? await res.json() : await res.text();

  if (!res.ok) {
    const msg =
      (typeof data === 'object' && data && 'error' in data
        ? (data as { error?: { message?: string } }).error?.message
        : null) ?? `Error ${res.status}`;
    throw new ApiError(msg, res.status, data);
  }
  return data as T;
}

export const apiUrl = (path: string) => `${BASE}${path}`;
